import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Search, Plus, Users, TrendingUp, Mail, Phone, IndianRupee, X, Pencil, Briefcase, FileText, Calendar, Trash2 } from 'lucide-react'
import { getClients, createClient as createClientSvc, updateClient as updateClientSvc, deleteClient as deleteClientSvc } from '../services/clients.service'
import { getInvoicesByClient } from '../services/invoices.service'
import { getProjectsByClient } from '../services/projects.service'
import { useToast } from '../context/ToastContext'
import { useActivity } from '../context/ActivityContext'
import { useIsAdmin } from '../hooks/useIsAdmin'
import type { Client } from '../types'

// ── Add Client Modal ───────────────────────────────────────────
const ALL_SERVICES = ['Google Ads', 'Meta Ads', 'SEO', 'Email', 'Social', 'Content', 'Web Dev', 'Strategy']

function AddClientModal({ onClose, onAdd }: { onClose: () => void; onAdd: (c: Omit<Client, 'id'>) => Promise<void> }) {
  const [form, setForm] = useState({
    name: '', contact: '', email: '', phone: '',
    mrr: '', status: 'prospect' as Client['status'],
    services: [] as string[],
  })
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(false)

  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setError('') }

  const toggleService = (s: string) =>
    setForm(f => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter(x => x !== s) : [...f.services, s],
    }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim())    { setError('Company name is required.'); return }
    if (!form.contact.trim()) { setError('Contact person is required.'); return }
    if (!form.email.trim())   { setError('Email is required.'); return }
    const mrrVal = form.status === 'prospect' ? 0 : parseInt(form.mrr.replace(/[^0-9]/g, '')) || 0
    const newClient = {
      name:     form.name.trim(),
      contact:  form.contact.trim(),
      email:    form.email.trim(),
      phone:    form.phone.trim(),
      mrr:      mrrVal,
      status:   form.status,
      services: form.services,
      joinedAt: new Date().toISOString().split('T')[0],
    }
    setLoading(true)
    setError('')
    try {
      await onAdd(newClient as Omit<Client, 'id'>)
      onClose()   // only close after successful save
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save client'
      setError(msg)
      setLoading(false)
    }
  }

  const inputCls = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelStyle = { color: '#a0a0a0' }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>New Client</h2>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        {error && (
          <div className="text-xs px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(204,51,0,0.12)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>COMPANY NAME *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Astra Fintech"
              className={inputCls} style={inputStyle} autoFocus
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>CONTACT PERSON *</label>
              <input value={form.contact} onChange={e => set('contact', e.target.value)} placeholder="Full name"
                className={inputCls} style={inputStyle}
                onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>STATUS</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} className={inputCls} style={inputStyle}>
                <option value="prospect">Prospect</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>EMAIL *</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="contact@company.com"
              className={inputCls} style={inputStyle}
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>PHONE</label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 98000 00000"
                className={inputCls} style={inputStyle}
                onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
            </div>
            {form.status !== 'prospect' && (
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>MONTHLY RETAINER (₹)</label>
                <input value={form.mrr} onChange={e => set('mrr', e.target.value)} placeholder="e.g. 150000"
                  className={inputCls} style={inputStyle}
                  onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold mb-2" style={labelStyle}>SERVICES</label>
            <div className="flex flex-wrap gap-1.5">
              {ALL_SERVICES.map(s => (
                <button type="button" key={s} onClick={() => toggleService(s)}
                  className="px-2.5 py-1 rounded-lg text-xs font-medium transition-all"
                  style={form.services.includes(s)
                    ? { background: '#ffc600', color: '#000' }
                    : { background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.15)' }}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)', opacity: loading ? 0.5 : 1 }}>
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
              style={{ background: loading ? 'rgba(255,198,0,0.6)' : '#ffc600', color: '#000', cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? (
                <>
                  <span className="inline-block w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Saving…
                </>
              ) : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function formatINR(n: number) {
  if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + 'L'
  if (n >= 1000) return '₹' + (n / 1000).toFixed(0) + 'k'
  return '₹' + n
}

const STATUS_STYLE: Record<Client['status'], { bg: string; color: string }> = {
  active:   { bg: 'rgba(232,160,0,0.12)', color: '#e8a000' },
  inactive: { bg: 'rgba(85,85,85,0.2)',   color: '#555' },
  prospect: { bg: 'rgba(255,198,0,0.1)',  color: '#ffc600' },
}

function ServiceTag({ label }: { label: string }) {
  return (
    <span className="text-xs px-2 py-0.5 rounded"
      style={{ background: 'rgba(255,198,0,0.08)', color: '#ffc600' }}>
      {label}
    </span>
  )
}

function ClientModal({ client, onClose, onUpdate, onDelete, isAdmin }: {
  client: Client
  onClose: () => void
  onUpdate: (id: string, changes: Partial<Client>) => void
  onDelete: (id: string) => void
  isAdmin: boolean
}) {
  const st = STATUS_STYLE[client.status]
  const [tab, setTab] = useState<'overview' | 'invoices' | 'projects'>('overview')
  const [editing, setEditing]           = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [form, setForm] = useState({
    name: client.name, contact: client.contact,
    email: client.email, phone: client.phone,
    status: client.status, mrr: client.mrr.toString(),
    services: [...client.services],
  })
  const setF = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  const toggleSvc = (s: string) => setForm(f => ({
    ...f, services: f.services.includes(s) ? f.services.filter(x => x !== s) : [...f.services, s],
  }))
  const handleSaveEdit = () => {
    onUpdate(client.id, {
      name: form.name.trim() || client.name,
      contact: form.contact.trim() || client.contact,
      email: form.email.trim() || client.email,
      phone: form.phone.trim(),
      status: form.status as Client['status'],
      mrr: parseInt(form.mrr.replace(/[^0-9]/g, '')) || 0,
      services: form.services,
    })
    setEditing(false)
  }

  // Related data — fetched from Supabase when modal opens
  const [clientInvoices, setClientInvoices] = useState<import('../types').Invoice[]>([])
  const [clientProjects, setClientProjects] = useState<import('../types').Project[]>([])
  useEffect(() => {
    getInvoicesByClient(client.id).then(setClientInvoices).catch(console.error)
    getProjectsByClient(client.id).then(setClientProjects).catch(console.error)
  }, [client.id])
  const invoiceTotal = clientInvoices.reduce((s, i) => s + i.total, 0)

  const INV_STATUS: Record<string, { bg: string; color: string }> = {
    paid:    { bg: 'rgba(50,200,100,0.12)',  color: '#32c864' },
    sent:    { bg: 'rgba(255,198,0,0.12)',   color: '#ffc600' },
    draft:   { bg: 'rgba(85,85,85,0.2)',     color: '#555' },
    overdue: { bg: 'rgba(204,51,0,0.12)',    color: '#cc3300' },
  }
  const PROJ_STATUS: Record<string, { bg: string; color: string }> = {
    'planning':    { bg: 'rgba(85,85,85,0.2)',   color: '#555' },
    'in-progress': { bg: 'rgba(255,198,0,0.12)', color: '#ffc600' },
    'review':      { bg: 'rgba(255,140,0,0.12)', color: '#ff8c00' },
    'done':        { bg: 'rgba(50,200,100,0.12)',color: '#32c864' },
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b flex-shrink-0" style={{ borderColor: 'rgba(255,198,0,0.12)' }}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0"
              style={{ background: '#ffc600', color: '#000' }}>
              {client.name[0]}
            </div>
            <div>
              <h2 className="text-base font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{client.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs px-2 py-0.5 rounded capitalize" style={{ background: st.bg, color: st.color }}>
                  {client.status}
                </span>
                <span className="text-xs" style={{ color: '#555' }}>
                  Since {new Date(client.joinedAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editing && isAdmin && (
              <button onClick={() => setEditing(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600', border: '1px solid rgba(255,198,0,0.2)' }}>
                <Pencil size={11} />Edit
              </button>
            )}
            <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-px flex-shrink-0" style={{ background: 'rgba(255,198,0,0.06)' }}>
          {[
            { label: 'MRR', value: client.status === 'prospect' ? '—' : formatINR(client.mrr), color: '#ffc600' },
            { label: 'Invoices', value: clientInvoices.length.toString(), color: '#e8a000' },
            { label: 'Projects', value: clientProjects.length.toString(), color: '#e8a000' },
          ].map(({ label, value, color }) => (
            <div key={label} className="px-4 py-3 text-center" style={{ background: '#0f0f0f' }}>
              <p className="text-base font-bold font-syne" style={{ color }}>{value}</p>
              <p className="text-xs mt-0.5" style={{ color: '#555' }}>{label}</p>
            </div>
          ))}
        </div>

        {/* Tabs — hidden while editing */}
        {!editing && (
          <div className="flex gap-1 px-5 pt-4 pb-0 flex-shrink-0">
            {(['overview', 'invoices', 'projects'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all"
                style={tab === t
                  ? { background: '#ffc600', color: '#000' }
                  : { background: '#1a1a1a', color: '#555' }}>
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Tab content / Edit form */}
        <div className="overflow-y-auto flex-1 p-5">
        {editing ? (
          /* ── Edit form ── */
          (() => {
            const iCls = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
            const iSty = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
            const lSty = { color: '#a0a0a0' }
            const focusOn  = (e: React.FocusEvent<any>) => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')
            const focusOff = (e: React.FocusEvent<any>) => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')
            return (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={lSty}>COMPANY NAME</label>
                  <input value={form.name} onChange={e => setF('name', e.target.value)} className={iCls} style={iSty} onFocus={focusOn} onBlur={focusOff} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={lSty}>CONTACT PERSON</label>
                    <input value={form.contact} onChange={e => setF('contact', e.target.value)} className={iCls} style={iSty} onFocus={focusOn} onBlur={focusOff} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={lSty}>STATUS</label>
                    <select value={form.status} onChange={e => setF('status', e.target.value)} className={iCls} style={iSty}>
                      <option value="active">Active</option>
                      <option value="prospect">Prospect</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={lSty}>EMAIL</label>
                  <input value={form.email} onChange={e => setF('email', e.target.value)} className={iCls} style={iSty} onFocus={focusOn} onBlur={focusOff} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={lSty}>PHONE</label>
                    <input value={form.phone} onChange={e => setF('phone', e.target.value)} className={iCls} style={iSty} onFocus={focusOn} onBlur={focusOff} />
                  </div>
                  {form.status !== 'prospect' && (
                    <div>
                      <label className="block text-xs font-semibold mb-1.5" style={lSty}>MRR (₹)</label>
                      <input value={form.mrr} onChange={e => setF('mrr', e.target.value)} placeholder="e.g. 150000" className={iCls} style={iSty} onFocus={focusOn} onBlur={focusOff} />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-2" style={lSty}>SERVICES</label>
                  <div className="flex flex-wrap gap-1.5">
                    {ALL_SERVICES.map(s => (
                      <button type="button" key={s} onClick={() => toggleSvc(s)}
                        className="px-2.5 py-1 rounded-lg text-xs font-medium transition-all"
                        style={form.services.includes(s)
                          ? { background: '#ffc600', color: '#000' }
                          : { background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.15)' }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )
          })()
        ) : (
          <>
          {tab === 'overview' && (
            <div className="space-y-4">
              <div className="space-y-2.5">
                {[
                  { icon: Users,    label: client.contact },
                  { icon: Mail,     label: client.email },
                  { icon: Phone,    label: client.phone || '—' },
                  { icon: Calendar, label: `Joined ${new Date(client.joinedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}` },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2.5 text-sm">
                    <Icon size={14} style={{ color: '#ffc600', flexShrink: 0 }} />
                    <span style={{ color: '#a0a0a0' }}>{label}</span>
                  </div>
                ))}
              </div>
              {client.services.length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: '#555' }}>SERVICES</p>
                  <div className="flex flex-wrap gap-1.5">
                    {client.services.map(s => <ServiceTag key={s} label={s} />)}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'invoices' && (
            <div>
              {clientInvoices.length === 0 ? (
                <p className="text-sm text-center py-8" style={{ color: '#555' }}>No invoices for this client.</p>
              ) : (
                <>
                  <div className="space-y-2">
                    {clientInvoices.map(inv => {
                      const s = INV_STATUS[inv.status] ?? INV_STATUS.draft
                      return (
                        <div key={inv.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg"
                          style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.08)' }}>
                          <div className="flex items-center gap-2.5">
                            <FileText size={13} style={{ color: '#555' }} />
                            <div>
                              <p className="text-xs font-semibold text-white">{inv.invoiceNumber}</p>
                              <p className="text-xs" style={{ color: '#555' }}>{inv.issuedAt}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2.5">
                            <span className="text-sm font-semibold text-white">{formatINR(inv.total)}</span>
                            <span className="text-xs px-2 py-0.5 rounded capitalize" style={{ background: s.bg, color: s.color }}>
                              {inv.status}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="flex justify-between items-center mt-3 pt-3 border-t text-sm"
                    style={{ borderColor: 'rgba(255,198,0,0.08)' }}>
                    <span style={{ color: '#a0a0a0' }}>Total billed</span>
                    <span className="font-bold font-syne" style={{ color: '#ffc600' }}>{formatINR(invoiceTotal)}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === 'projects' && (
            <div>
              {clientProjects.length === 0 ? (
                <p className="text-sm text-center py-8" style={{ color: '#555' }}>No projects for this client.</p>
              ) : (
                <div className="space-y-2">
                  {clientProjects.map(proj => {
                    const s = PROJ_STATUS[proj.status] ?? PROJ_STATUS['planning']
                    return (
                      <div key={proj.id} className="px-3 py-2.5 rounded-lg"
                        style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.08)' }}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-white">{proj.title}</p>
                          <span className="text-xs px-2 py-0.5 rounded capitalize" style={{ background: s.bg, color: s.color }}>
                            {proj.status}
                          </span>
                        </div>
                        <div className="h-1 rounded-full" style={{ background: '#2a2a2a' }}>
                          <div className="h-full rounded-full" style={{ width: `${proj.progress}%`, background: '#ffc600' }} />
                        </div>
                        <p className="text-xs mt-1.5" style={{ color: '#555' }}>
                          {proj.progress}% · due {new Date(proj.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
          </>
        )}
        </div>

        <div className="px-5 pb-5 flex-shrink-0">
          {editing ? (
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                Discard
              </button>
              <button onClick={handleSaveEdit}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#ffc600', color: '#000' }}>
                Save Changes
              </button>
            </div>
          ) : confirmDelete ? (
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(false)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                Cancel
              </button>
              {isAdmin && (
                <button onClick={() => { onDelete(client.id); onClose() }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: 'rgba(204,51,0,0.15)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>
                  Yes, Remove
                </button>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              {isAdmin && (
                <button onClick={() => setConfirmDelete(true)}
                  className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold flex-shrink-0"
                  style={{ background: '#1a1a1a', color: '#cc3300', border: '1px solid rgba(204,51,0,0.2)' }}>
                  <Trash2 size={14} />
                </button>
              )}
              <button onClick={onClose} className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function Clients() {
  const [clients, setClients] = useState<Client[]>([])
  const [search, setSearch]   = useState('')
  const [statusFilter, setStatusFilter] = useState<Client['status'] | 'all'>('all')
  const [selected, setSelected] = useState<Client | null>(null)
  const [showAdd, setShowAdd]   = useState(false)

  const { toast }       = useToast()
  const { addActivity } = useActivity()
  const isAdmin         = useIsAdmin()
  const location        = useLocation()

  useEffect(() => {
    getClients().then(setClients).catch(console.error)
    if ((location.state as any)?.openAdd) {
      setShowAdd(true)
      window.history.replaceState({}, '')
    }
  }, [])

  const handleAddClient = async (c: Omit<Client, 'id'>) => {
    const saved = await createClientSvc(c)   // throws on error — modal catches it and shows inline
    setClients(prev => [saved, ...prev])
    toast(`${saved.name} added as ${saved.status}`)
    addActivity({ type: 'new_client', emoji: '🤝', description: `New client: ${saved.name}`, detail: `${saved.contact} · ₹${saved.mrr.toLocaleString('en-IN')}/mo` })
  }

  // Clients show ALL — no date range filter (you always need to see your full client list)
  const filtered = clients.filter(c => {
    const matchSearch = search === '' ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.contact.toLowerCase().includes(search.toLowerCase())
    const matchStatus = statusFilter === 'all' || c.status === statusFilter
    return matchSearch && matchStatus
  })

  const totalMRR  = clients.filter(c => c.status === 'active').reduce((s, c) => s + c.mrr, 0)
  const activeCount = clients.filter(c => c.status === 'active').length
  const prospectCount = clients.filter(c => c.status === 'prospect').length

  return (
    <div className="p-6 min-h-full" style={{ background: '#000' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-syne text-white">Clients</h1>
          <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>{clients.length} total · {activeCount} active · {prospectCount} prospects</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: '#ffc600', color: '#000' }}>
            <Plus size={15} /> Add Client
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total MRR', value: formatINR(totalMRR), icon: IndianRupee, color: '#ffc600' },
          { label: 'Active Clients', value: activeCount.toString(), icon: Users, color: '#e8a000' },
          { label: 'Prospects', value: prospectCount.toString(), icon: TrendingUp, color: '#ff8c00' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="rounded-xl p-4 flex items-center gap-3"
            style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${color}18` }}>
              <Icon size={17} style={{ color }} />
            </div>
            <div>
              <p className="text-xl font-bold font-syne text-white">{value}</p>
              <p className="text-xs" style={{ color: '#a0a0a0' }}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#555' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search clients or contacts…"
            className="w-full pl-9 pr-4 py-2 rounded-lg text-sm outline-none text-white placeholder-gray-600"
            style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }} />
        </div>
        <div className="flex gap-1.5">
          {(['all', 'active', 'inactive', 'prospect'] as const).map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium capitalize"
              style={statusFilter === s
                ? { background: '#ffc600', color: '#000' }
                : { background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(client => {
          const st = STATUS_STYLE[client.status]
          return (
            <div key={client.id}
              className="rounded-xl p-5 cursor-pointer transition-all"
              style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.3)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.12)')}
              onClick={() => setSelected(client)}>

              {/* Top row */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
                    style={{ background: '#ffc600', color: '#000' }}>
                    {client.name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-bold font-syne text-white">{client.name}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#a0a0a0' }}>{client.contact}</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-0.5 rounded capitalize flex-shrink-0"
                  style={{ background: st.bg, color: st.color }}>
                  {client.status}
                </span>
              </div>

              {/* MRR */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs mb-0.5" style={{ color: '#555' }}>MRR</p>
                  <p className="text-lg font-bold font-syne" style={{ color: '#ffc600' }}>
                    {client.status === 'prospect' ? '—' : formatINR(client.mrr)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs mb-0.5" style={{ color: '#555' }}>SINCE</p>
                  <p className="text-sm font-medium text-white">
                    {new Date(client.joinedAt).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })}
                  </p>
                </div>
              </div>

              {/* Services */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {client.services.slice(0, 3).map(s => <ServiceTag key={s} label={s} />)}
                {client.services.length > 3 && (
                  <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(85,85,85,0.2)', color: '#555' }}>
                    +{client.services.length - 3}
                  </span>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid rgba(255,198,0,0.06)' }}>
                <div className="flex items-center gap-1.5 text-xs" style={{ color: '#555' }}>
                  <Mail size={11} />{client.email}
                </div>
                {isAdmin && (
                  <button className="p-1.5 rounded transition-colors" style={{ color: '#ffc600' }}
                    onClick={e => { e.stopPropagation(); setSelected(client) }}>
                    <Pencil size={12} />
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16" style={{ color: '#555' }}>
          <Briefcase size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No clients match your search.</p>
        </div>
      )}

      {/* Client detail modal */}
      {selected && (
        <ClientModal
          client={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onUpdate={async (id, changes) => {
            try {
              await updateClientSvc(id, changes)
              setClients(prev => prev.map(c => c.id === id ? { ...c, ...changes } : c))
              setSelected(prev => prev?.id === id ? { ...prev, ...changes } as Client : prev)
              toast(`${selected.name} updated`, 'success')
              addActivity({ type: 'client_updated', emoji: '✏️', description: `Client updated: ${selected.name}`, detail: changes.status ? `Status → ${changes.status}` : 'Details updated' })
            } catch { toast('Update failed', 'error') }
          }}
          onDelete={async id => {
            const c = clients.find(x => x.id === id)
            try {
              await deleteClientSvc(id)
              setClients(prev => prev.filter(x => x.id !== id))
              setSelected(null)
              if (c) {
                toast(`${c.name} removed`, 'info')
                addActivity({ type: 'client_deleted', emoji: '🗑️', description: `Client removed: ${c.name}`, detail: c.contact })
              }
            } catch { toast('Delete failed', 'error') }
          }}
        />
      )}
      {showAdd && <AddClientModal onClose={() => setShowAdd(false)} onAdd={handleAddClient} />}
    </div>
  )
}
